import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

/** Animated trend line SVG that visualizes growth across scale. */
const TrendChartBackground = () => {
  const curvePath =
    "M 0 280 C 80 278, 200 270, 320 255 S 520 200, 640 160 S 800 80, 900 30 L 900 30";
  const fillPath =
    "M 0 280 C 80 278, 200 270, 320 255 S 520 200, 640 160 S 800 80, 900 30 L 900 300 L 0 300 Z";

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg
        viewBox="0 0 900 300"
        fill="none"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
      >
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,77,46,0.18)" />
            <stop offset="60%" stopColor="rgba(255,77,46,0.06)" />
            <stop offset="100%" stopColor="rgba(255,77,46,0)" />
          </linearGradient>
          <linearGradient id="trendStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,77,46,0.1)" />
            <stop offset="50%" stopColor="rgba(255,77,46,0.35)" />
            <stop offset="100%" stopColor="rgba(255,77,46,0.5)" />
          </linearGradient>
        </defs>
        {[30, 60, 90, 120, 150, 180, 210, 240].map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="900"
            y2={y}
            stroke="rgba(17,17,24,0.15)"
            strokeWidth="0.5"
            strokeDasharray="3 5"
          />
        ))}
        <motion.path
          d={fillPath}
          fill="url(#trendFill)"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.2 }}
        />
        <motion.path
          d={curvePath}
          stroke="url(#trendStroke)"
          strokeWidth="1.5"
          strokeLinecap="round"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.5, delay: 0.4, ease: "easeOut" }}
        />
      </svg>
    </div>
  );
};

export interface ScaleSectionProps {
  /** Small caps label above the headline. */
  eyebrow?: string;
  /**
   * The first highlighted word — italic red, in-line with the headline. The
   * surrounding prose hooks into it via {first}/{second} slots so callers
   * can change the framing without rebuilding the JSX.
   */
  firstHighlight?: string;
  /** Headline fragment before {first}. */
  leadIn?: string;
  /** Headline fragment between {first} and {second}. */
  middle?: React.ReactNode;
  /** The second highlighted word. */
  secondHighlight?: string;
  /** Headline fragment after {second}. */
  tail?: React.ReactNode;
  /** Supporting subtitle below the headline. */
  subtitle?: React.ReactNode;
  /** Counter strip values; the last one is rendered as the accent. */
  counters?: string[];
  /** Optional one-line cross-link, set on its own under the counter strip. */
  footnote?: React.ReactNode;
}

const DEFAULTS: Required<
  Omit<ScaleSectionProps, "middle" | "tail" | "subtitle" | "footnote">
> & {
  middle: React.ReactNode;
  tail: React.ReactNode;
  subtitle: React.ReactNode;
} = {
  eyebrow: "Built for any scale",
  leadIn: "Whether it’s ",
  firstHighlight: "one",
  middle: (
    <>
      {" "}
      high-impact experience
      <br className="hidden md:block" /> or{" "}
    </>
  ),
  secondHighlight: "one hundred thousand",
  tail: (
    <>
      &thinsp;&mdash;
      <br />
      you decide what&rsquo;s worth sharing.
    </>
  ),
  subtitle: (
    <>
      Hand-craft one for your most important relationship. Or send personalized
      narratives to your entire audience. Same platform, same polish.
    </>
  ),
  counters: ["1", "50", "500", "5K", "50K", "100K+"],
};

/**
 * Growing-trendline section. Originally a flexibility statement for the
 * marketing home page ("one VIP or one hundred thousand"); now also reused
 * on use-case pages with prop overrides for the headline and counter strip.
 */
export const ScaleSection = (props: ScaleSectionProps = {}) => {
  const {
    eyebrow = DEFAULTS.eyebrow,
    leadIn = DEFAULTS.leadIn,
    firstHighlight = DEFAULTS.firstHighlight,
    middle = DEFAULTS.middle,
    secondHighlight = DEFAULTS.secondHighlight,
    tail = DEFAULTS.tail,
    subtitle = DEFAULTS.subtitle,
    counters = DEFAULTS.counters,
    footnote,
  } = props;

  return (
    <section className="bg-fwd-white py-24 md:py-32 px-8 md:px-16 lg:px-20 relative overflow-hidden">
      <TrendChartBackground />
      <div className="max-w-5xl mx-auto text-center relative z-10">
        <Reveal>
          <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-6">
            {eyebrow}
          </span>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="font-display text-[clamp(2.2rem,4.5vw,4rem)] font-normal leading-[1.1] text-fwd-ink mb-10">
            {leadIn}
            <span className="inline-flex items-baseline">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="font-display italic text-fwd-red"
              >
                {firstHighlight}
              </motion.span>
            </span>
            {middle}
            <span className="inline-flex items-baseline">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="font-display italic text-fwd-red"
              >
                {secondHighlight}
              </motion.span>
            </span>
            {tail}
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="text-fwd-mid text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-light">
            {subtitle}
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-16 flex items-center justify-center gap-6 md:gap-16 flex-wrap bg-fwd-white/30 backdrop-blur-xs rounded-xl px-6 py-1 mx-auto w-fit">
            {counters.map((n, i) => (
              <motion.div
                key={n + i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
                className={`font-display leading-none ${
                  i === counters.length - 1
                    ? "text-3xl md:text-5xl text-fwd-red"
                    : "text-2xl md:text-3xl text-fwd-ink/30"
                }`}
              >
                {n}
              </motion.div>
            ))}
          </div>
        </Reveal>

        {footnote && (
          <Reveal delay={0.35}>
            <p className="mt-14 text-fwd-ink/70 text-base md:text-lg">
              {footnote}
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};
